import React, { useEffect, useRef } from "react";
import QRCodeStyling from "qr-code-styling";
import { toStylingOptions } from "@/lib/qr";

// Compact brand watermark drawn INSIDE the lower-right of the QR (Basic plan).
const WatermarkChip = () => {
  const style = { fontSize: "clamp(6px, 3.4cqw, 12px)" };
  return (
    <div
      data-testid="qr-watermark"
      className="absolute rounded-md bg-white/95 border border-black/10 px-1 py-0.5 leading-tight text-center pointer-events-none select-none shadow-sm"
      style={{ right: "6%", bottom: "6%" }}
    >
      <div className="font-semibold text-neutral-900" style={style}>QR Code</div>
      <div className="font-semibold text-neutral-900" style={style}>Creator Studio</div>
    </div>
  );
};

// Renders a live QR using qr-code-styling. Preview == download output.
export default function QRPreview({ design, data, className = "", frameless = false, watermark = false }) {
  const ref = useRef(null);
  const qrRef = useRef(null);
  // Match export: force High error correction when watermarking so the chip stays scannable.
  const effectiveDesign = watermark ? { ...(design || {}), ecc: "H" } : design;

  useEffect(() => {
    qrRef.current = new QRCodeStyling(toStylingOptions(effectiveDesign, data));
    if (ref.current) {
      ref.current.innerHTML = "";
      qrRef.current.append(ref.current);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (qrRef.current) qrRef.current.update(toStylingOptions(effectiveDesign, data));
    // eslint-disable-next-line
  }, [design, data, watermark]);

  const d = design || {};
  const frame = frameless ? "none" : d.frame || "none";
  const inner = <div ref={ref} data-testid="qr-canvas" className="[&>svg]:block [&>svg]:w-full [&>svg]:h-auto [&>canvas]:w-full [&>canvas]:h-auto" style={{ width: "100%" }} />;
  const chip = watermark ? <WatermarkChip /> : null;

  if (frame === "none") {
    return (
      <div className={className}>
        <div className="relative" style={{ containerType: "inline-size" }}>{inner}{chip}</div>
      </div>
    );
  }

  const frameLabelTop = frame === "scan-top" || frame === "badge";
  const frameStyle = { borderColor: d.frameColor || "#6366F1", background: d.frameBg || "#FFFFFF" };
  return (
    <div className={className}>
      <div className="rounded-2xl p-3 border-2 inline-block w-full" style={frameStyle}>
        {frameLabelTop && (
          <div className="text-center mb-2 rounded-lg py-1.5 text-sm font-bold tracking-widest"
            style={{ background: d.frameColor || "#6366F1", color: d.frameTextColor || "#fff" }}>
            {d.frameText || "SCAN ME"}
          </div>
        )}
        <div className="rounded-lg overflow-hidden relative" style={{ containerType: "inline-size" }}>{inner}{chip}</div>
        {(frame === "scan-bottom" || frame === "phone") && (
          <div className="text-center mt-2 rounded-lg py-1.5 text-sm font-bold tracking-widest"
            style={{ background: d.frameColor || "#6366F1", color: d.frameTextColor || "#fff" }}>
            {d.frameText || "SCAN ME"}
          </div>
        )}
      </div>
    </div>
  );
}
